import React, { useState } from 'react';
import { ExternalLink, Lock } from 'lucide-react';
import { deckImage } from '../components/deck/deckAssets';

const INTERNAL_PASSWORD = 'joboticsinternal';
const INTERNAL_LINKS = [
  { name: 'Fundraising data room', url: 'https://drive.google.com/drive/folders/1-ATrIClyCHkzGgiYz70N_VD60daR7IeG' },
  { name: 'Investment memo', url: 'https://docs.google.com/document/d/1QeI7EiuN_BheJYEOqezuF6bXvcD_c0qL' },
  { name: 'GTM', url: 'https://docs.google.com/spreadsheets/d/1NTeDOb148Xcccfs0QL6Y6VspdN14YKf2fuczVquFpdc/edit?gid=481777863#gid=481777863' },
  { name: 'Board facing deck', url: 'https://docs.google.com/presentation/d/1IH8RCImscMe1xTBXMwWUnt_z4cfMSwqe/edit' },
];

const INVESTOR_PASSWORD = 'jobotics2026';
const INVESTOR_LINKS = [
  { name: 'Investor deck', url: 'https://demo.jobotics.ai/deck/' },
  { name: 'Fundraising data room', url: 'https://drive.google.com/drive/folders/1-ATrIClyCHkzGgiYz70N_VD60daR7IeG' },
  { name: 'Investment memo', url: 'https://docs.google.com/document/d/1QeI7EiuN_BheJYEOqezuF6bXvcD_c0qL' },
];

/** Column 1: Intro — 3 Data challenges, Client deck, Partner deck */
const INTRO_ITEMS = [
  {
    name: '3 Data challenges',
    description: 'Robert Dong on LinkedIn — AI in construction has 3 big data problems: fragmentation, dual entry, and data entry discipline.',
    url: 'https://www.linkedin.com/feed/update/urn:li:activity:7430612208541319168/',
  },
  {
    name: 'Client deck',
    description: 'Deck tailored for contractors — case studies, deployment timeline, and client-facing messaging.',
    url: '/client-deck',
  },
  {
    name: 'Partner deck',
    description: 'Partnership and co-sell focus — Skill Grid, integration opportunities, and partner-facing messaging.',
    url: '/partner-deck',
  },
];

/** Column 2: Solution — Context graph, Skill grid, Workflow demo */
const SOLUTION_ITEMS = [
  {
    name: 'Construction Context graph',
    description: 'Interactive view of the construction context graph — entities and relationships across your project data.',
    url: 'https://sylvan.jobotics.ai/api/agent-demo/view/dash-1772936413860-ft2v',
  },
  {
    name: 'Skill grid',
    description: 'Construction systems we support — PM, ERP, field, safety, documents, CRM. Open APIs and integrations.',
    url: '/skill-grid',
  },
  {
    name: 'Workflow demo',
    description: 'Live demo — labor estimation, contract redline, ERP analyst, and voice agent. Ask questions and run workflows.',
    url: 'https://demo.jobotics.ai/',
  },
];

/**
 * Public gallery: intro + two columns — Intro (3 Data challenges, Client deck, Partner deck),
 * Solution (Context graph, Skill grid, Workflow demo). Access at /gallery.
 */
const STORAGE_KEY_INTERNAL = 'gallery-internal-unlocked';
const STORAGE_KEY_INVESTOR = 'gallery-investor-unlocked';

export default function Gallery() {
  const [internalModalOpen, setInternalModalOpen] = useState(false);
  const [internalUnlocked, setInternalUnlocked] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const [investorModalOpen, setInvestorModalOpen] = useState(false);
  const [investorUnlocked, setInvestorUnlocked] = useState(false);
  const [investorPasswordInput, setInvestorPasswordInput] = useState('');
  const [investorPasswordError, setInvestorPasswordError] = useState('');

  const openInternalModal = () => {
    setInternalModalOpen(true);
    setPasswordError('');
    setPasswordInput('');
    try {
      setInternalUnlocked(sessionStorage.getItem(STORAGE_KEY_INTERNAL) === '1');
    } catch (_) {}
  };

  const closeInternalModal = () => {
    setInternalModalOpen(false);
    setPasswordError('');
  };

  const submitInternalPassword = (e) => {
    e.preventDefault();
    if (passwordInput.trim() === INTERNAL_PASSWORD) {
      try {
        sessionStorage.setItem(STORAGE_KEY_INTERNAL, '1');
      } catch (_) {}
      setInternalUnlocked(true);
      setPasswordError('');
      setPasswordInput('');
    } else {
      setPasswordError('Incorrect password.');
    }
  };

  const openInvestorModal = () => {
    setInvestorModalOpen(true);
    setInvestorPasswordError('');
    setInvestorPasswordInput('');
    try {
      setInvestorUnlocked(sessionStorage.getItem(STORAGE_KEY_INVESTOR) === '1');
    } catch (_) {}
  };

  const closeInvestorModal = () => {
    setInvestorModalOpen(false);
    setInvestorPasswordError('');
  };

  const submitInvestorPassword = (e) => {
    e.preventDefault();
    if (investorPasswordInput.trim() === INVESTOR_PASSWORD) {
      try {
        sessionStorage.setItem(STORAGE_KEY_INVESTOR, '1');
      } catch (_) {}
      setInvestorUnlocked(true);
      setInvestorPasswordError('');
      setInvestorPasswordInput('');
    } else {
      setInvestorPasswordError('Incorrect password.');
    }
  };
  return (
    <div className="min-h-screen bg-gray-50">
      <header className="border-b border-gray-200 bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4 sm:px-6">
          <div className="flex items-center gap-3">
            <img src={deckImage('/jobotics-logo.png')} alt="Jobotics" className="h-10 w-auto" />
            <h1 className="text-xl font-bold text-gray-900">Portal Gallery</h1>
          </div>
          <div className="flex items-center gap-4">
            <a
              href="https://sylvan.jobotics.ai/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-medium text-primary-600 hover:text-primary-700 hover:underline"
            >
              Sylvan
            </a>
            <a
              href="https://jobotics-egan.up.railway.app/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-medium text-primary-600 hover:text-primary-700 hover:underline"
            >
              Egan
            </a>
            <a
              href="https://fieldwire.jobotics.ai/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-medium text-primary-600 hover:text-primary-700 hover:underline"
            >
              Fieldwire
            </a>
            <button
              type="button"
              onClick={openInvestorModal}
              className="flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
            >
              <Lock className="h-4 w-4" />
              Investor
            </button>
            <button
              type="button"
              onClick={openInternalModal}
              className="flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
            >
              <Lock className="h-4 w-4" />
              Internal
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-10 sm:px-6 sm:py-14">
        {/* Jobotics intro summary */}
        <section className="mb-14 text-center">
          <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            Jobotics — The Answer & Action Engine for Construction
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg leading-relaxed text-gray-600">
            One place for your project data: ask questions, get answers, and run workflow and SOP
            automation—without switching tools. We connect your existing systems (Procore, ACC,
            Fieldwire, Vista, and more) so office and field see the same picture.
          </p>
        </section>

        {/* Two columns: Intro (3 cards) | Solution (3 cards) */}
        <section className="mb-14">
          <div className="grid grid-cols-2 gap-8">
            <div>
              <h3 className="mb-4 text-lg font-semibold text-gray-900">Intro</h3>
              <div className="space-y-4">
                {INTRO_ITEMS.map((item) => (
                  <a
                    key={item.name}
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex flex-col rounded-2xl border-2 border-primary-500 bg-white p-6 shadow-lg transition hover:border-primary-600 hover:shadow-xl"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <h4 className="text-lg font-bold text-gray-900 group-hover:text-primary-600">
                        {item.name}
                      </h4>
                      <ExternalLink className="h-5 w-5 shrink-0 text-gray-400 group-hover:text-primary-500" />
                    </div>
                    <p className="mt-3 flex-1 text-sm leading-relaxed text-gray-600">
                      {item.description}
                    </p>
                  </a>
                ))}
              </div>
            </div>
            <div>
              <h3 className="mb-4 text-lg font-semibold text-gray-900">Solution</h3>
              <div className="space-y-4">
                {SOLUTION_ITEMS.map((item) => (
                  <a
                    key={item.name}
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex flex-col rounded-2xl border-2 border-primary-500 bg-white p-6 shadow-lg transition hover:border-primary-600 hover:shadow-xl"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <h4 className="text-lg font-bold text-gray-900 group-hover:text-primary-600">
                        {item.name}
                      </h4>
                      <ExternalLink className="h-5 w-5 shrink-0 text-gray-400 group-hover:text-primary-500" />
                    </div>
                    <p className="mt-3 flex-1 text-sm leading-relaxed text-gray-600">
                      {item.description}
                    </p>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Internal links modal */}
      {internalModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={closeInternalModal}>
          <div
            className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            {internalUnlocked ? (
              <>
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Internal links</h3>
                  <button
                    type="button"
                    onClick={closeInternalModal}
                    className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                    aria-label="Close"
                  >
                    ×
                  </button>
                </div>
                <ul className="space-y-2">
                  {INTERNAL_LINKS.map((item) => (
                    <li key={item.name}>
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-between gap-2 rounded-lg border border-gray-200 px-4 py-3 text-sm font-medium text-gray-900 hover:border-primary-500 hover:bg-primary-50"
                      >
                        {item.name}
                        <ExternalLink className="h-4 w-4 shrink-0 text-gray-400" />
                      </a>
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <>
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Internal</h3>
                  <button
                    type="button"
                    onClick={closeInternalModal}
                    className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                    aria-label="Close"
                  >
                    ×
                  </button>
                </div>
                <form onSubmit={submitInternalPassword}>
                  <label htmlFor="internal-password" className="mb-2 block text-sm font-medium text-gray-700">
                    Password
                  </label>
                  <input
                    id="internal-password"
                    type="password"
                    value={passwordInput}
                    onChange={(e) => setPasswordInput(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                    placeholder="Enter password"
                    autoFocus
                  />
                  {passwordError && (
                    <p className="mt-2 text-sm text-red-600">{passwordError}</p>
                  )}
                  <div className="mt-4 flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={closeInternalModal}
                      className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
                    >
                      Unlock
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      {/* Investor links modal */}
      {investorModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={closeInvestorModal}>
          <div
            className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            {investorUnlocked ? (
              <>
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Investor links</h3>
                  <button
                    type="button"
                    onClick={closeInvestorModal}
                    className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                    aria-label="Close"
                  >
                    ×
                  </button>
                </div>
                <ul className="space-y-2">
                  {INVESTOR_LINKS.map((item) => (
                    <li key={item.name}>
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-between gap-2 rounded-lg border border-gray-200 px-4 py-3 text-sm font-medium text-gray-900 hover:border-primary-500 hover:bg-primary-50"
                      >
                        {item.name}
                        <ExternalLink className="h-4 w-4 shrink-0 text-gray-400" />
                      </a>
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <>
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Investor</h3>
                  <button
                    type="button"
                    onClick={closeInvestorModal}
                    className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                    aria-label="Close"
                  >
                    ×
                  </button>
                </div>
                <form onSubmit={submitInvestorPassword}>
                  <label htmlFor="investor-password" className="mb-2 block text-sm font-medium text-gray-700">
                    Password
                  </label>
                  <input
                    id="investor-password"
                    type="password"
                    value={investorPasswordInput}
                    onChange={(e) => setInvestorPasswordInput(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                    placeholder="Enter password"
                    autoFocus
                  />
                  {investorPasswordError && (
                    <p className="mt-2 text-sm text-red-600">{investorPasswordError}</p>
                  )}
                  <div className="mt-4 flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={closeInvestorModal}
                      className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
                    >
                      Unlock
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
